/**
 * @fileoverview `VenuePanelSnippet` (WEB-717) — the venue block that mounts as
 * the third satellite section inside `EventDecisionPanel`'s `children` slot,
 * between the mini-map (WEB-716) and the getting-there rows (WEB-718).
 *
 * Measured design: `docs/design-system/event-v2-m3-panel-satellite-specs.md`
 * §2 (`data-dc-tpl` 195). Kicker (mono caps, teal) → venue name (`<h3>`,
 * display 18px/600) → an optional two-column stat grid → the "See the venue →"
 * affordance. Section padding `18px 20px`; the 1px bottom rule rides
 * {@link PANEL_SECTION_CLASS} (WEB-738), never a hand-written border, and is
 * deliberately NOT suppressed when this is the last section — the card's own
 * `[&>*:last-child]:border-b-0` handles that structurally in every combination
 * of present/absent satellites.
 *
 * `RunOfDatesStrip` (WEB-712) is the shipped precedent for this shape: a
 * molecule that carries its own section chrome, returns `null` when its gate
 * fails, and mounts bare into `children`. `EventDecisionPanel.tsx` is NOT
 * edited by this issue (the Linear "Files affected" list predates WEB-738).
 *
 * ## Deviation from the design's markup — one destination, one accessible name
 *
 * The design wraps the entire block in a single `<a>` whose subtree holds both
 * the `<h3>` and a separate "See the venue →" line. That gives one destination
 * two competing accessible names. Shipped instead (spec §2.1's own
 * instruction):
 *
 * - The `<a>` sits on the **venue name**, inside the semantic `<h3>`, so its
 *   accessible name and its visible text agree (WCAG 2.5.3 Label in Name).
 * - `after:absolute after:inset-0` stretches that one anchor's hit area over
 *   the whole section, so the design's whole-block click target is preserved
 *   while Tab still stops exactly once. `relative` on the root is load-bearing:
 *   without a positioned ancestor the pseudo-element would resolve against the
 *   initial containing block and cover the viewport. Same pattern, and same
 *   reasoning, as `DiscoverEventCard`'s `.dv-ev__link::after`.
 * - "See the venue →" is `aria-hidden` decorative text, never a second link.
 *
 * Box metrics are unchanged by any of this — the pseudo-element is
 * out-of-flow and the CTA keeps its `margin-top: 12px`.
 *
 * ## Honest absence — never a placeholder stat
 *
 * A `—` in a stat slot is the exact defect this component exists to prevent.
 * Two backed stats render `1fr 1fr`; **one** renders a single column (never a
 * `1fr 1fr` grid with an empty sibling); **zero** emits no stat grid element at
 * all — matching the design's own degraded render, which drops the section from
 * 168.5px to 114.4px by removing the grid rather than zeroing it (spec §2.2).
 *
 * The component renders only the stats it is handed and never derives one.
 * {@link resolveVenuePanelStats} is the single sanctioned derivation — see its
 * docblock for what is backed by a real column and what is not.
 *
 * ## Kicker
 *
 * `getEventLexicon(slug).venueKicker`, resolved by the caller and passed in —
 * this file never branches on a slug and never re-derives the lexicon. Spec
 * §2.3 records a shipped-vs-design divergence on four slugs (`food`, `art`,
 * `drinks`, `hobbies-interests` ship "Place" where the design says "Venue");
 * that is tracked as its own issue and is explicitly out of scope here.
 *
 * Server-safe: no hooks, no event handlers, no `'use client'` — a plain `<a>`,
 * the same posture as `EventModuleSection`.
 */

import type { CSSProperties, ReactElement } from 'react';

import type { CityCode } from '@loci/city-editions';

import { TextIcon } from '@/components/atoms/TextIcon';
import { cn } from '@/components/atoms/primitives/utils';
import { PANEL_SECTION_CLASS } from '@/components/organisms/EventDecisionPanel';
import { cityUrl } from '@/lib/cross-host-urls';
import {
  readLocationVenueSlug,
  readResolvablePageSlug,
} from '@/lib/eventDetail/readResolvablePageSlug';
import { EVT_MICRO_LABEL_10_MONO_CLASS, EVT_MICRO_LABEL_NOTE_CLASS } from '@/lib/eventV2MicroLabel';
import type { EventDetailV2 } from '@/types/eventDetail';

/** Literal CTA copy, per spec §2.1. The `→` is decorative, not part of a label. */
const CTA_LABEL = 'See the venue';

/** The design's stat grid never exceeds two columns (spec §2.1). */
const MAX_STATS = 2;

/**
 * One stat cell: a display-family value over a mono uppercase label.
 *
 * Both strings are already render-ready — this component formats nothing, so a
 * caller cannot smuggle a unit or a placeholder past it by handing over a
 * number and hoping for a label.
 */
export interface VenuePanelStat {
  /** Rendered value, e.g. `"4,200"` or `"Since 1936"`. */
  value: string;
  /** Mono uppercase label, e.g. `"Capacity"` or `"Years active"`. */
  label: string;
}

export interface VenuePanelSnippetProps {
  /**
   * Venue display name. Blank/absent → the whole block renders `null`
   * (spec §6): there is no honest venue snippet without a venue.
   */
  venueName: string | null | undefined;
  /**
   * `getEventLexicon(categorySlug).venueKicker` — "Venue" or "Place".
   * Caller-resolved; never derived here (spec §2.3).
   */
  kicker: string;
  /** City whose marketing host owns `/venue/<slug>` — e.g. `'CLT'`. */
  cityCode: CityCode;
  /**
   * Venue slug. Absent/blank/placeholder → the name renders as plain text and
   * the CTA is suppressed entirely, never a dead `#` or `/venue/venue`
   * (WEB-328 / WEB-809).
   */
  venueSlug?: string | null;
  /**
   * Zero, one, or two caller-resolved stats — see {@link resolveVenuePanelStats}.
   * Anything past the first two is dropped rather than wrapped onto a third
   * column the design has no measurement for.
   */
  stats?: readonly VenuePanelStat[];
  className?: string;
}

/** Trim-and-narrow: `undefined`/`null`/whitespace all collapse to `null`. */
function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Generated event-detail `location` plus the pre-contract `venueSlug` extra
 * that Storybook / RTL fixtures still set. Canonical `slug` and the extra
 * are both read through {@link readLocationVenueSlug}.
 */
type VenuePanelLocation = NonNullable<EventDetailV2['location']> & {
  venueSlug?: string | null;
};

/** The data half of {@link VenuePanelSnippetProps}, derived from one event. */
export interface VenuePanelSnippetSource {
  venueName: string | null;
  venueSlug: string | null;
  stats: VenuePanelStat[];
}

/**
 * The **only** sanctioned derivation of this block's content, and the reason it
 * exists is to make the not-backed list unmissable.
 *
 * Stats backed by a real column today (spec §2.4):
 *
 * | Stat | Source | Rendered |
 * | --- | --- | --- |
 * | Capacity | `location.capacity` | grouped integer, e.g. `4,200` |
 * | Years active | `location.foundedYear` | `Since 1936` |
 *
 * **Not backed — do not invent a mapping.** Home dates left · Typical spend
 * (`$$`/`$$$`) · Floor · Stages · Year running · Classes a week · Greenway
 * stretch · Games on site · Nights a week · an upcoming-events-at-this-venue
 * count (no such field exists on the event-detail payload) · "Typical range"
 * (`meta.priceMin`/`priceMax` is **this event's** price, not the venue's
 * typical range — labelling it so would be a fabrication).
 *
 * So for most categories the honest result is one stat or none, and the design
 * itself proves that reads as deliberate rather than broken (§2.2).
 *
 * Stat order is fixed — Capacity, then Years active — so the same venue never
 * renders its two facts in a different order on two renders.
 *
 * The caller supplies `kicker` and `cityCode` itself:
 *
 * ```tsx
 * <VenuePanelSnippet
 *   {...resolveVenuePanelSnippetSource(event)}
 *   kicker={getEventLexicon(categorySlug).venueKicker}
 *   cityCode={cityCode}
 * />
 * ```
 */
export function resolveVenuePanelSnippetSource(event: EventDetailV2): VenuePanelSnippetSource {
  const location = (event.location ?? null) as VenuePanelLocation | null;
  const stats: VenuePanelStat[] = [];

  const capacity = location?.capacity;
  // `> 0` on purpose: a persisted `0` is "not enriched", not a venue that
  // holds nobody, and rendering "0 / CAPACITY" would be the placeholder this
  // component exists to avoid.
  if (typeof capacity === 'number' && Number.isFinite(capacity) && capacity > 0) {
    stats.push({ value: capacity.toLocaleString('en-US'), label: 'Capacity' });
  }

  const foundedYear = location?.foundedYear;
  if (typeof foundedYear === 'number' && Number.isInteger(foundedYear) && foundedYear > 0) {
    stats.push({ value: `Since ${foundedYear}`, label: 'Years active' });
  }

  return {
    venueName: nonEmpty(location?.name),
    venueSlug: readLocationVenueSlug(location),
    stats,
  };
}

/**
 * Line-height 1.45 on every line of this block.
 *
 * The design declares no `line-height` in the venue block — it inherits the
 * mockup's own 1.45, which is the Event v2 house value (`EventModuleSection`'s
 * `<h2>` and the decision panel's `<dd>` already state it explicitly). Left
 * unstated here, Tailwind's `text-[Npx]` supplies 1.5 and the `<h3>` would take
 * whatever it was given, which measured the block 7.7px short of the design's
 * 168.48px in real Chromium. Stated per element rather than inherited from the
 * root because each element's own `text-[Npx]` otherwise supplies its own.
 */
const LEADING_CLASS = 'leading-[1.45]';

/**
 * The repo token for the design's `--teal` (spec §4 pins the hexes but not a
 * repo token — that gap was resolved across the three M3 PRs in favour of this
 * one). `--clt-skyline-glow-fg` resolves to **`#4FC4C4` in dark**, byte-identical
 * to the spec's dark `--teal`, and to `#0A6363` in light — deliberately darker
 * than the design's `#0E7B7B` so small 10px caps clear AA on the panel surface.
 *
 * `GettingThereCard` already uses it, and WEB-716/718 use it for the same
 * design token, so all three M3 satellites paint one teal inside one card.
 * Do not substitute `--accent` or paste the hex.
 */
const TEAL_TEXT_CLASS = 'text-clt-skyline-glow-fg';

/** Display-family 18px/600 name, `margin: 6px 0 0` (spec §2.1). */
const NAME_CLASS = cn(
  'm-0 mt-[6px] text-h6 font-semibold wrap-break-word text-fg-1 font-display',
  LEADING_CLASS,
);

/**
 * The stretched anchor. `text-inherit` keeps the name at `--fg-1` through
 * the `a { color: inherit }` reset (styles/base.css), and the `::after`
 * overlay is what reproduces the design's whole-block hit area from a single
 * link — see the file header.
 *
 * The `!` on the two underline utilities was load-bearing. That same reset
 * is `a { color: inherit; text-decoration: none }` (then unlayered in
 * globals.css; layered in styles/base.css since WEB-853), and an unlayered
 * author declaration beats every `@layer utilities` rule at
 * any specificity — so a plain `hover:underline` compiles fine and then never
 * paints. Verified in Chromium, not assumed. The `focus-visible:outline-*`
 * rules need no `!`: the reset does not touch `outline`.
 */
const NAME_LINK_CLASS = cn(
  'text-inherit no-underline',
  "after:absolute after:inset-0 after:content-['']",
  'hover:underline! focus-visible:underline!',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1',
);

/**
 * `VenuePanelSnippet` — a self-contained panel section. Returns `null` when no
 * venue name is known; see the file header for the full degrade contract.
 */
export function VenuePanelSnippet({
  venueName,
  kicker,
  cityCode,
  venueSlug,
  stats,
  className,
}: VenuePanelSnippetProps): ReactElement | null {
  const name = nonEmpty(venueName);
  if (name === null) {
    return null;
  }

  const slug = readResolvablePageSlug(venueSlug);
  // Per the issue's AC: the destination is built with `cityUrl()` and lands on
  // the marketing-city host. `EventDetailTemplate.tsx:656-673` links to the
  // same page relatively (`/venue/<slug>`); both forms resolve, and the
  // inconsistency is called out in the PR body rather than silently picked.
  const href = slug === null ? null : cityUrl(cityCode, `/venue/${encodeURIComponent(slug)}`);

  const renderedStats = (stats ?? []).slice(0, MAX_STATS);
  // One stat renders ONE column — never `1fr 1fr` with an empty sibling.
  const statGridStyle: CSSProperties = {
    gridTemplateColumns: renderedStats.length === MAX_STATS ? '1fr 1fr' : '1fr',
  };

  return (
    <div
      // `relative` is required by the stretched-link `::after`; `group` scopes
      // the hover treatment to the whole section, which is exactly the area
      // that overlay makes clickable.
      className={cn(PANEL_SECTION_CLASS, 'group relative flex flex-col px-5 py-[18px]', className)}
      data-testid="venue-panel-snippet"
    >
      <p
        data-testid="venue-panel-snippet-kicker"
        // Teal, NOT `--fg-3` (spec §2.1). See TEAL_TOKEN.
        className={cn(EVT_MICRO_LABEL_10_MONO_CLASS, LEADING_CLASS, TEAL_TEXT_CLASS)}
      >
        {kicker}
      </p>

      <h3 className={NAME_CLASS} data-testid="venue-panel-snippet-name">
        {href === null ? (
          name
        ) : (
          <a href={href} className={NAME_LINK_CLASS} data-testid="venue-panel-snippet-link">
            {name}
          </a>
        )}
      </h3>

      {renderedStats.length > 0 ? (
        <div
          className="mt-3 grid gap-3"
          style={statGridStyle}
          data-testid="venue-panel-snippet-stats"
        >
          {renderedStats.map((stat) => (
            <div key={stat.label} className="min-w-0" data-testid="venue-panel-snippet-stat">
              <p
                data-testid="venue-panel-snippet-stat-value"
                className={cn('m-0 text-[19px] font-bold text-fg-1 font-display', LEADING_CLASS)}
              >
                {stat.value}
              </p>
              <p
                data-testid="venue-panel-snippet-stat-label"
                className={cn(EVT_MICRO_LABEL_NOTE_CLASS, LEADING_CLASS, 'm-0 text-fg-3')}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      {href === null ? null : (
        // Decorative affordance, not a second destination: the anchor above
        // already covers this area and already carries the accessible name.
        // `aria-hidden` is what keeps one destination to one name.
        <p
          aria-hidden="true"
          data-testid="venue-panel-snippet-cta"
          className={cn(
            'm-0 mt-3 inline-flex items-center gap-[6px] text-small font-semibold font-display',
            TEAL_TEXT_CLASS,
            LEADING_CLASS,
          )}
        >
          {CTA_LABEL}
          <span className="transition-transform duration-standard group-hover:translate-x-0.5 motion-reduce:transition-none">
            <TextIcon />
          </span>
        </p>
      )}
    </div>
  );
}
